import { TypedDocumentNode, gql } from '@apollo/client';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { PullToRefetch } from '@sorare/core/src/atoms/navigation/PullToRefetch';
import { extractConnectionData } from '@sorare/core/src/gql/extractData';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';

import { Auction } from 'components/auction/Auction';

import type {
  OpenTokenAuctionsQuery,
  OpenTokenAuctionsQueryVariables,
} from './__generated__/index.graphql';
import { DisplayItems } from '../../common/DisplayItems';
import { OpenSaleSortType } from '../../common/types';

const OPEN_TOKEN_AUCTIONS_QUERY = gql`
  query OpenTokenAuctionsQuery(
    $cursor: String
    $newlyListed: Boolean
    $sport: [Sport!]
  ) {
    currentUser {
      slug
      involvedLiveTokenAuctions(
        newlyListed: $newlyListed
        after: $cursor
        sport: $sport
      ) {
        nodes {
          id
          ...Auction_auction
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
  ${Auction.fragments.auction}
` as TypedDocumentNode<OpenTokenAuctionsQuery, OpenTokenAuctionsQueryVariables>;

export const OpenAuctions = ({
  sortType,
  sport,
}: {
  sortType: OpenSaleSortType;
  sport: Sport[];
}) => {
  const newlyListed = sortType === OpenSaleSortType.NEWLY_LISTED;
  const { loading, data, refetch, loadMore } = usePaginatedQuery(
    OPEN_TOKEN_AUCTIONS_QUERY,
    {
      connection: 'TokenAuctionConnection',
      variables: {
        newlyListed,
        sport,
      },
    }
  );

  const { items, count, hasMore, cursor } = extractConnectionData(
    data?.currentUser?.involvedLiveTokenAuctions,
    p => <Auction key={p.id} auction={p} />
  );

  const displayLoading = !items && loading;

  return (
    <PullToRefetch refetch={refetch}>
      <DisplayItems
        displayLoading={displayLoading}
        count={count}
        items={items}
        loadMoreSection={{
          hasMore,
          loading,
          loadMore: () => {
            loadMore(false, {
              cursor,
              newlyListed,
              sport,
            });
          },
        }}
      />
    </PullToRefetch>
  );
};
